// =============================================================================
// Finance widget bodies — budget totals for the current month and savings
// account balances. All of these are read-only: none call updateData.
// =============================================================================

import React from 'react';
import { format, parseISO } from 'date-fns';
import { formatDate as fmt } from '../../../lib/dates';
import { Wallet, TrendingDown, TrendingUp, PiggyBank } from 'lucide-react';
import {
  formatEUR,
  formatEURShort,
  StatTile,
  WidgetList,
  WidgetRow,
  WidgetBar,
  WidgetEmpty,
  isCompact
} from '../shared';

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const asDate = (value) => {
  if (!value) return null;
  try {
    const d = typeof value === 'string' ? parseISO(value) : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

// Items without a parseable date are dropped, exactly as Budget.js's
// isInSelectedMonth does.
const monthKeyOf = (value) => {
  const d = asDate(value);
  return d ? format(d, 'yyyy-MM') : null;
};

const sumAmounts = (items) =>
  (items || []).reduce((acc, item) => acc + num(item && item.amount), 0);

const safeToday = (today) =>
  today instanceof Date && !Number.isNaN(today.getTime()) ? today : new Date();

const monthLabel = (today) => fmt(safeToday(today), 'month');

// Budget.js's balance card is income − expenses − savings over the selected
// month; the dashboard always uses the current one.
const monthTotals = (data, today) => {
  const key = format(safeToday(today), 'yyyy-MM');
  const budget = (data && data.budget) || {};
  const pick = (list) =>
    (list || []).filter((item) => item && monthKeyOf(item.date) === key);

  const income = pick(budget.income);
  const expenses = pick(budget.expenses);
  const savings = pick(budget.savings);

  return {
    income,
    expenses,
    savings,
    totalIncome: sumAmounts(income),
    totalExpenses: sumAmounts(expenses),
    totalSavings: sumAmounts(savings)
  };
};

// How many rows of ~rowPx fit into the widget body. Grid rows are 44px with
// 12px gutters and the card header eats roughly one row.
const rowsThatFit = (size, rowPx) => {
  const h = Number(size && size.h) || 4;
  const inner = h * 44 + Math.max(0, h - 1) * 12 - 44;
  return Math.max(1, Math.floor(inner / rowPx));
};

// -----------------------------------------------------------------------------
// Budget stat tiles
// -----------------------------------------------------------------------------

const BudgetRemaining = ({ data, size, today, t }) => {
  const { totalIncome, totalExpenses, totalSavings } = monthTotals(data, today);
  const balance = totalIncome - totalExpenses - totalSavings;
  const compact = isCompact(size);

  return (
    <StatTile
      icon={Wallet}
      compact={compact}
      value={compact ? formatEURShort(balance) : formatEUR(balance)}
      label={t('dashw.budget.remaining', 'Übrig diesen Monat')}
      sub={monthLabel(today)}
      tone={balance >= 0 ? 'positive' : 'negative'}
    />
  );
};

const BudgetExpenses = ({ data, size, today, t }) => {
  const { totalExpenses } = monthTotals(data, today);
  const compact = isCompact(size);

  return (
    <StatTile
      icon={TrendingDown}
      compact={compact}
      value={compact ? formatEURShort(totalExpenses) : formatEUR(totalExpenses)}
      label={t('dashw.budget.expenses', 'Ausgaben')}
      sub={monthLabel(today)}
      tone="negative"
    />
  );
};

const BudgetIncome = ({ data, size, today, t }) => {
  const { totalIncome } = monthTotals(data, today);
  const compact = isCompact(size);

  return (
    <StatTile
      icon={TrendingUp}
      compact={compact}
      value={compact ? formatEURShort(totalIncome) : formatEUR(totalIncome)}
      label={t('dashw.budget.income', 'Einnahmen')}
      sub={monthLabel(today)}
      tone="positive"
    />
  );
};

// -----------------------------------------------------------------------------
// Top expense categories
// -----------------------------------------------------------------------------

const BudgetCategories = ({ data, size, today, t }) => {
  const { expenses, totalExpenses } = monthTotals(data, today);
  const budget = (data && data.budget) || {};
  // Legacy data kept the list under `categories` (mirrors Budget.js).
  const categories = budget.expenseCategories || budget.categories || [];

  const totalsById = new Map();
  expenses.forEach((item) => {
    const id = item.category || '';
    totalsById.set(id, (totalsById.get(id) || 0) + num(item.amount));
  });

  const limit = Math.min(5, rowsThatFit(size, 46));
  const rows = Array.from(totalsById.entries())
    .filter(([, amount]) => amount > 0)
    .map(([id, amount]) => {
      const category = categories.find((c) => c && c.id === id);
      return {
        id: id || '__uncategorized__',
        amount,
        name: (category && category.name) || t('dashw.budget.uncategorized', 'Ohne Kategorie'),
        color: category && category.color
      };
    })
    .sort((a, b) => b.amount - a.amount)
    .slice(0, limit);

  if (rows.length === 0) {
    return (
      <WidgetEmpty>
        {t('dashw.budget.noExpenses', 'Keine Ausgaben in diesem Monat')}
      </WidgetEmpty>
    );
  }

  return (
    <WidgetList>
      {rows.map((row) => (
        <WidgetRow key={row.id}>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2 mb-1">
              <span className="text-sm text-gray-900 dark:text-gray-100 truncate" title={row.name}>
                {row.name}
              </span>
              <span className="text-sm tabular-nums text-gray-600 dark:text-gray-300 flex-shrink-0">
                {formatEUR(row.amount)}
              </span>
            </div>
            <WidgetBar
              pct={totalExpenses > 0 ? (row.amount / totalExpenses) * 100 : 0}
              color={row.color}
            />
          </div>
        </WidgetRow>
      ))}
    </WidgetList>
  );
};

// -----------------------------------------------------------------------------
// Recent bookings
// -----------------------------------------------------------------------------

const KIND_STYLES = {
  income: { sign: '+', className: 'text-green-600 dark:text-green-400' },
  expense: { sign: '−', className: 'text-red-600 dark:text-red-400' },
  savings: { sign: '−', className: 'text-purple-600 dark:text-purple-400' }
};

const BudgetRecent = ({ data, size, today, t }) => {
  const { income, expenses, savings } = monthTotals(data, today);
  const compact = isCompact(size);

  const tag = (items, kind) =>
    items.map((item, index) => {
      const date = asDate(item.date);
      return {
        key: `${kind}-${item.id != null ? item.id : index}`,
        kind,
        name: item.name || item.source || t('dashw.budget.unnamed', 'Ohne Namen'),
        amount: num(item.amount),
        time: date ? date.getTime() : 0,
        date
      };
    });

  const limit = Math.min(8, rowsThatFit(size, 37));
  const rows = [...tag(income, 'income'), ...tag(expenses, 'expense'), ...tag(savings, 'savings')]
    .sort((a, b) => b.time - a.time)
    .slice(0, limit);

  if (rows.length === 0) {
    return (
      <WidgetEmpty>
        {t('dashw.budget.noBookings', 'Keine Buchungen in diesem Monat')}
      </WidgetEmpty>
    );
  }

  return (
    <WidgetList>
      {rows.map((row) => {
        const style = KIND_STYLES[row.kind] || KIND_STYLES.expense;
        return (
          <WidgetRow key={row.key}>
            <span
              className="text-sm text-gray-900 dark:text-gray-100 truncate flex-1 min-w-0"
              title={row.name}
            >
              {row.name}
            </span>
            {!compact && row.date && (
              <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums flex-shrink-0">
                {fmt(row.date, 'short')}
              </span>
            )}
            <span className={`text-sm font-medium tabular-nums flex-shrink-0 ${style.className}`}>
              {style.sign}
              {formatEUR(Math.abs(row.amount))}
            </span>
          </WidgetRow>
        );
      })}
    </WidgetList>
  );
};

// -----------------------------------------------------------------------------
// Savings accounts
// -----------------------------------------------------------------------------

// Savings.js adds budget savings entries on top of the stored balance. Its own
// fallback also matches account and entry names as substrings, which pulls in
// unrelated entries (and matches everything for unnamed ones), so the dashboard
// only counts entries that explicitly point at the account.
const accountBalance = (account, budgetSavings) =>
  num(account && account.balance) +
  (budgetSavings || []).reduce(
    (acc, entry) =>
      entry && account && entry.accountId && entry.accountId === account.id
        ? acc + num(entry.amount)
        : acc,
    0
  );

const SavingsBalance = ({ data, scope, size, t }) => {
  const budget = (data && data.budget) || {};
  const accounts = budget.savingsAccounts || [];
  const budgetSavings = budget.savings || [];
  const compact = isCompact(size);

  if (accounts.length === 0) {
    return (
      <WidgetEmpty>{t('dashw.savings.noAccounts', 'Noch keine Sparkonten')}</WidgetEmpty>
    );
  }

  // Unknown or missing scopes fall back to the grand total.
  const account =
    scope && scope !== 'total' ? accounts.find((a) => a && a.id === scope) : null;

  const value = account
    ? accountBalance(account, budgetSavings)
    : accounts.reduce((acc, a) => acc + accountBalance(a, budgetSavings), 0);

  const label = account
    ? account.name || t('dashw.savings.unnamedAccount', 'Konto')
    : t('dashw.savings.total', 'Sparen gesamt');

  const sub = account
    ? undefined
    : accounts.length === 1
      ? t('dashw.savings.accountOne', '1 Konto')
      : t('dashw.savings.accountCount', '{count} Konten', { count: accounts.length });

  return (
    <StatTile
      icon={PiggyBank}
      compact={compact}
      value={compact ? formatEURShort(value) : formatEUR(value)}
      label={label}
      sub={sub}
      tone={value >= 0 ? 'accent' : 'negative'}
    />
  );
};

const SavingsAccounts = ({ data, t }) => {
  const budget = (data && data.budget) || {};
  const accounts = budget.savingsAccounts || [];
  const budgetSavings = budget.savings || [];

  if (accounts.length === 0) {
    return (
      <WidgetEmpty>{t('dashw.savings.noAccounts', 'Noch keine Sparkonten')}</WidgetEmpty>
    );
  }

  const rows = accounts
    .filter(Boolean)
    .map((account, index) => ({
      key: account.id != null ? account.id : `account-${index}`,
      name: account.name || t('dashw.savings.unnamedAccount', 'Konto'),
      balance: accountBalance(account, budgetSavings)
    }));

  const total = rows.reduce((acc, row) => acc + row.balance, 0);

  return (
    <WidgetList>
      {rows.map((row) => (
        <WidgetRow key={row.key}>
          <span
            className="text-sm text-gray-900 dark:text-gray-100 truncate flex-1 min-w-0"
            title={row.name}
          >
            {row.name}
          </span>
          <span className="text-sm tabular-nums text-gray-600 dark:text-gray-300 flex-shrink-0">
            {formatEUR(row.balance)}
          </span>
        </WidgetRow>
      ))}
      <WidgetRow>
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate flex-1 min-w-0">
          {t('dashw.savings.totalRow', 'Gesamt')}
        </span>
        <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100 flex-shrink-0">
          {formatEUR(total)}
        </span>
      </WidgetRow>
    </WidgetList>
  );
};

export const FINANCE_BODIES = {
  'budget.remaining': BudgetRemaining,
  'budget.expenses': BudgetExpenses,
  'budget.income': BudgetIncome,
  'budget.categories': BudgetCategories,
  'budget.recent': BudgetRecent,
  'savings.balance': SavingsBalance,
  'savings.accounts': SavingsAccounts
};
